import { FORGE_MASTER_LABEL_PATH } from '../paths';
import type { CleaningIssueScope } from '../types';

export type MasterLabelUrlParams = {
  chainId: number;
  designId?: string | null;
  itemNumber?: string | null;
  /** Extra item numbers to open alongside `itemNumber` (extra `in` params). */
  extraItemNumbers?: string[];
  normalizedBarcode?: string | null;
  /** Extra unresolved normalized barcodes to open alongside `normalizedBarcode`. */
  extraNormalizedBarcodes?: string[];
  cropIds?: string[];
  /** Second evidence set rendered in the highlight mode's secondary color (`c2` params). */
  secondaryCropIds?: string[];
  /** Third evidence set rendered in the highlight mode's tertiary color (`c3` params). */
  tertiaryCropIds?: string[];
  highlightMode?:
    | 'similar'
    | 'different'
    | 'bad_quality'
    | 'peer_groups'
    | 'fragmented'
    | 'coherence_confusion';
  highlightScope?: CleaningIssueScope;
};

export type MasterLabelHighlightMode = NonNullable<MasterLabelUrlParams['highlightMode']>;

export function appendMasterLabelHighlightParams(
  params: URLSearchParams,
  primaryCropCount: number,
  highlightMode?: MasterLabelHighlightMode | null,
  highlightScope?: CleaningIssueScope | null
): void {
  if (!highlightMode || primaryCropCount <= 0) return;
  params.set('m', highlightMode);
  if (highlightScope) params.set('ms', highlightScope);
}

export function itemNumberFromMasterLabelUrl(masterLabelUrl?: string | null): string | null {
  if (!masterLabelUrl) return null;
  try {
    return new URL(masterLabelUrl, window.location.origin).searchParams.get('in');
  } catch {
    const paramsText = masterLabelUrl.split('?')[1];
    if (!paramsText) return null;
    return new URLSearchParams(paramsText).get('in');
  }
}

function appendUniqueExtras(
  params: URLSearchParams,
  key: string,
  primary: string | null | undefined,
  extras: string[] | undefined
): void {
  const uniqueExtras = [...new Set((extras ?? []).map((value) => value.trim()).filter(Boolean))];
  uniqueExtras.forEach((value) => {
    if (value !== primary) params.append(key, value);
  });
}

export function buildMasterLabelUrl({
  chainId,
  designId,
  itemNumber,
  extraItemNumbers,
  normalizedBarcode,
  extraNormalizedBarcodes,
  cropIds,
  secondaryCropIds,
  tertiaryCropIds,
  highlightMode,
  highlightScope,
}: MasterLabelUrlParams): string {
  const params = new URLSearchParams({ chain: String(chainId) });
  if (designId) params.append('d', designId);
  if (itemNumber) params.append('in', itemNumber);
  appendUniqueExtras(params, 'in', itemNumber, extraItemNumbers);
  if (normalizedBarcode) params.append('nb', normalizedBarcode);
  appendUniqueExtras(params, 'nb', normalizedBarcode, extraNormalizedBarcodes);
  const uniqueCropIds = [
    ...new Set((cropIds ?? []).map((cropId) => cropId.trim()).filter(Boolean)),
  ];
  uniqueCropIds.forEach((cropId) => {
    params.append('c', cropId);
  });
  const primaryCropIdSet = new Set(uniqueCropIds);
  const uniqueSecondaryCropIds = [
    ...new Set((secondaryCropIds ?? []).map((cropId) => cropId.trim()).filter(Boolean)),
  ].filter((cropId) => !primaryCropIdSet.has(cropId));
  uniqueSecondaryCropIds.forEach((cropId) => {
    params.append('c2', cropId);
  });
  const usedCropIdSet = new Set([...uniqueCropIds, ...uniqueSecondaryCropIds]);
  const uniqueTertiaryCropIds = [
    ...new Set((tertiaryCropIds ?? []).map((cropId) => cropId.trim()).filter(Boolean)),
  ].filter((cropId) => !usedCropIdSet.has(cropId));
  uniqueTertiaryCropIds.forEach((cropId) => {
    params.append('c3', cropId);
  });
  appendMasterLabelHighlightParams(params, uniqueCropIds.length, highlightMode, highlightScope);
  return `${FORGE_MASTER_LABEL_PATH}?${params.toString()}`;
}

export function normalizeMasterLabelUrl(
  masterLabelUrl: string | null | undefined,
  {
    chainId,
    fallbackItemNumber,
    requireTarget = false,
  }: {
    chainId: number;
    fallbackItemNumber?: string | null;
    requireTarget?: boolean;
  }
): string | null {
  const itemNumber = fallbackItemNumber || itemNumberFromMasterLabelUrl(masterLabelUrl) || null;
  if (!masterLabelUrl && requireTarget && !itemNumber) return null;

  try {
    const url = masterLabelUrl
      ? new URL(masterLabelUrl, window.location.origin)
      : new URL(FORGE_MASTER_LABEL_PATH, window.location.origin);
    url.pathname = FORGE_MASTER_LABEL_PATH;
    url.searchParams.set('chain', String(chainId));
    if (!url.searchParams.get('in') && itemNumber) {
      url.searchParams.set('in', itemNumber);
    }
    return `${url.pathname}${url.search}`;
  } catch {
    if (requireTarget && !itemNumber) return null;
    if (itemNumber) return buildMasterLabelUrl({ chainId, itemNumber });
    return buildMasterLabelUrl({ chainId });
  }
}
